import { lazy, Suspense, useEffect, useState, useMemo } from "react";
// import lazy from "react";
import { QueryClient, QueryClientProvider, useQuery } from "react-query";
import BackgroundDataLoader from "@components/BackgroundDataLoader";
import { Navigate, Route, Routes } from "react-router-dom";
// import Spinner from "@components/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useMutation } from "react-query";
//protcted and unprotected routes
/*
import ProtectedRoute from "@routes/ProtectedRoute";
*/
// import RouteUnauthenticated from "@routes/UnauthenticatedRoute";
import { ReactQueryDevtools } from "react-query/devtools";
import CookieConsent from "react-cookie-consent";
import jwt_decode from "jwt-decode";
import Spinner from "@components/Spinner/Spinner";
import useWindowDimensions from "@hooks/useWindowDimension";
import { mobileView, desktopView } from "./redux/viewSlice";
import Dashboard from "@modules/Dashboard";
import Header from "@components/Header/Header";
import PublicHeader from "@components/Header/PublicHeader";
import SensorPage from "@modules/Sensors/components";
import UserPage from "@modules/Users/components";
import { useLocation } from "react-router-dom";

import { actions } from "@modules/Auth/_redux/authRedux";
import { refresh } from "@modules/Auth/_redux/authCrud";
// import axiosInstance, { baseURL } from "@helper/interceptor";
// import { persistor } from "./redux/store";
// import { logPageVisit } from "./modules/Users/services/userCrud";
// import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";
// import { TourProvider } from "@reactour/tour";

const Login = lazy(() => import("@modules/Auth/components/Login"));
// import Login from "@modules/Auth/components/Login";
// import SettingPage from "@modules/Settings/components";
// import CustomerPage from "@modules/Customer/components";
// import PageNotFound from "@components/Error/PageNotFound";
// import jwt_decode from "jwt-decode";

const Signup = lazy(() => import("@modules/Auth/components/Signup"));
const CreateCustomer = lazy(() => import("@modules/Auth/components/CreateCustomer"));
const SignupSuccess = lazy(() => import("@modules/Auth/components/SignupSuccess"));
const ForgotPassword = lazy(() => import("@modules/Auth/components/ForgotPassword"));
const ResetPassword = lazy(() => import("@modules/Auth/components/ResetPassword"));
const SettingPage = lazy(() => import("@modules/Settings/components"));
const CustomerPage = lazy(() => import("@modules/Customer/components"));
const AdminCustomerPage = lazy(() => import("@modules/Admin/components"));
const SensorDetail = lazy(() => import("@modules/Sensors/components/SensorDetail"));
const PageNotFound = lazy(() => import("@components/Error/PageNotFound"));
const InventoryPage = lazy(() => import("@modules/Inventory"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep unused data in cache for 10 minutes
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on component mount if data exists
      retry: 1, // Only retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff

      // Concurrent request optimizations
      refetchOnReconnect: "always", // Refetch when network reconnects
      suspense: false, // Disable suspense to prevent blocking
      useErrorBoundary: false, // Handle errors gracefully

      // Network mode optimizations
      networkMode: "online", // Only run queries when online

      // Query scheduling
      queryKeyHashFn: (queryKey) => JSON.stringify(queryKey), // Efficient cache keys
    },
    mutations: {
      retry: 1, // Only retry failed mutations once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Mutation optimizations
      networkMode: "online",
      useErrorBoundary: false,
    },
  },
});

// const expiration = new Date(payload.exp);
// const now = new Date();
// const fiveMinutes = 1000 * 60 * 5;
//
// if (expiration.getTime() - now.getTime() < fiveMinutes) {
//   console.log("JWT has expired or will expire soon");
// } else {
//   console.log("JWT is valid for more than 5 minutes", payload);
// }

const PublicRoute = ({ children }) => {
  return (
    <div id="ts_wrapper">
      <PublicHeader />
      <main className="page-container">{children}</main>
    </div>
  );
};

const PaidOnlyRoute = ({ children }) => {
  const { auth } = useSelector((state) => state);

  // Check if user is authenticated
  if (!auth?.authToken || !auth?.user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow access if customer.paid is true
  if (!auth.user.paid) {
    console.log("[PaidOnlyRoute] User is not paid, redirecting to sensors");
    return <Navigate to="/sensors/list" replace />;
  }

  return children;
};

const UnpaidOnlyRoute = ({ children }) => {
  const { auth } = useSelector((state) => state);

  // Check if user is authenticated
  if (!auth?.authToken || !auth?.user) {
    return <Navigate to="/login" replace />;
  }

  // Only allow access if customer.paid is false (unpaid users)
  if (auth.user.paid) {
    console.log("[UnpaidOnlyRoute] User is paid, redirecting to sensors");
    return <Navigate to="/sensors/list" replace />;
  }

  return children;
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const auth = useSelector((state) => state.auth, shallowEqual);
  const dispatch = useDispatch();

  const { isLoading: loadingRefresh, mutateAsync: refreshMutate } = useMutation(refresh, {
    onSuccess: async (data) => {
      const accessToken = data.access;
      const refreshExp = data.refreshExp;
      const decoded = jwt_decode(accessToken);
      await dispatch(actions.login(accessToken));
      await dispatch(actions.refreshExp(refreshExp));
      await dispatch(actions.userLoaded(decoded));
    },
  });

  const { isAuthorized, isExpiredAccess, isExpiredRefresh, refreshExp } = useMemo(() => {
    return {
      isAuthorized: auth.authToken != null,
      isExpiredAccess: auth.authToken
        ? new Date(jwt_decode(auth.authToken).exp * 1000).getTime() < new Date().getTime()
        : false,
      isExpiredRefresh: auth.refreshExp
        ? new Date(auth.refreshExp * 1000).getTime() < new Date().getTime()
        : false,
      refreshExp: auth.refreshExp ? auth.refreshExp : false,
    };
  }, [auth.authToken, auth.refreshExp]);

  useEffect(() => {
    localStorage.setItem("refreshExp", refreshExp);
  }, [refreshExp]);

  useEffect(() => {
    if (isAuthorized && isExpiredAccess && !isExpiredRefresh && !loadingRefresh) {
      localStorage.setItem("loadingRefresh", "loading");
      refreshMutate({ refresh: auth.refreshToken })
        .then(() => localStorage.setItem("loadingRefresh", "done"))
        .catch(() => localStorage.setItem("loadingRefresh", "done"));
    }
  }, [
    isAuthorized,
    isExpiredAccess,
    isExpiredRefresh,
    loadingRefresh,
    refreshMutate,
    auth.refreshToken,
  ]);

  if (!isAuthorized || isExpiredRefresh) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (isAuthorized && isExpiredAccess && !isExpiredRefresh) {
    return <Spinner animation="border" size="sm" />;
  }

  return (
    <div id="ts_wrapper">
      <BackgroundDataLoader />
      <Header />
      <main className="page-container">{children}</main>
    </div>
  );
};

const UnprotectedRoute = ({ children }) => {
  const auth = useSelector((state) => state.auth, shallowEqual);

  const { isAuthorized, isExpiredAccess, refreshExp } = useMemo(() => {
    return {
      isAuthorized: auth.authToken != null,
      isExpiredAccess: auth.authToken
        ? new Date(jwt_decode(auth.authToken).exp * 1000).getTime() < new Date().getTime()
        : false,
      refreshExp: auth.refreshExp ? auth.refreshExp : false,
    };
  }, [auth.authToken, auth.refreshExp]);

  const { state } = useLocation();

  useEffect(() => {
    localStorage.setItem("refreshExp", refreshExp);
  }, [refreshExp]);

  if (isAuthorized && !isExpiredAccess) {
    const previousPath = localStorage.getItem("previousPath");
    const previousSearch = localStorage.getItem("previousSearch");
    let { from } = state || { from: { pathname: "/sensors/list", search: "" } };
    if (!state && previousPath) {
      from = { pathname: previousPath, search: previousSearch };
    }
    return <Navigate to={from.pathname + from.search} replace />;
  }

  return children;
};

const App = () => {
  const { i18n } = useTranslation();
  // const { isAuthorized } = useSelector(
  // 	({ auth }) => ({
  // 		isAuthorized: auth.authToken != null,
  // 	}),
  // 	shallowEqual
  // );
  // const steps = [
  // 	{
  // 		selector: "[data-tour='step-1']",
  // 		content: (
  // 			<p>
  // 				Hier siehst du dein <code>erstes Projekt</code>.
  // 			</p>
  // 		),
  // 	},
  // 	{
  // 		selector: "[data-tour='step-2']",
  // 		content: (
  // 			<p>
  // 				Hier siehst du die Bäume des Projekts als <code> Karte </code>.
  // 			</p>
  // 		),
  // 	},
  // 	{
  // 		selector: "[data-tour='step-3']",
  // 		content: (
  // 			<p>
  // 				Hier kannst du eine Zusammenfassung <code> verschiedener Statistiken </code>
  // 				sehen.
  // 			</p>
  // 		),
  // 	},
  // ];
  // let navigate = useNavigate();
  // const [step, setStep] = useState(0);
  //
  // const setCurrentStep = (step) => {
  // 	switch (step) {
  // 		case 0:
  // 			navigate("/projects", true);
  // 			break;
  // 		case 1:
  // 			navigate("/projects/mapview", true);
  // 			break;
  // 		case 2:
  // 			navigate("/dashboard", true);
  // 			break;
  // 		default:
  // 			break;
  // 	}
  // 	setStep(step);
  // };

  // const fallbackUri = `${isAuthorized ? "/projects" : "/login"}`;
  // const disableBody = () => {
  //   console.log("OPENED");
  // };
  // const enableBody = () => {
  //   console.log("CLOSED");
  // };

  const { auth } = useSelector((state) => state);
  // console.log("AUTH", auth);
  // console.log(mobile);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  // console.log(mobile);
  //language setting after login
  useEffect(() => {
    if (Object.keys(auth).length != 0 && auth.authToken != null && auth.user != null) {
      const { language } = auth.user;
      let lang = "";
      if (language === 0) lang = "de";
      if (language === 1) lang = "en";
      if (language === 2) lang = "it";
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("de");
    }
  }, [auth.authToken, auth.user, i18n]);

  //check the width of device to display mobile or desktop view
  useEffect(() => {
    if (width < 768) {
      dispatch(mobileView());
    } else {
      dispatch(desktopView());
    }
  }, [width]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* BackgroundDataLoader moved to ProtectedRoute to only run when authenticated */}
      {/* <TourProvider */}
      {/* 	steps={steps} */}
      {/* 	currentStep={step} */}
      {/* 	setCurrentStep={setCurrentStep} */}
      {/* 	afterOpen={disableBody} */}
      {/* 	beforeClose={enableBody} */}
      {/* > */}
      <Suspense fallback={<Spinner height={"80vh"} />}>
        <Routes>
          <Route
            path="/login"
            element={
              <UnprotectedRoute>
                <Login />
              </UnprotectedRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <UnprotectedRoute>
                <Signup />
              </UnprotectedRoute>
            }
          />
          <Route
            path="/signup-successful"
            element={
              <UnprotectedRoute>
                <SignupSuccess />
              </UnprotectedRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <UnprotectedRoute>
                <ForgotPassword />
              </UnprotectedRoute>
            }
          />
          <Route
            path="/password-reset/:type/:id"
            element={
              <UnprotectedRoute>
                <ResetPassword />
              </UnprotectedRoute>
            }
          />
          {/* <Route path="/authentication-mail-send" element = {<AuthenticationMailSend />}/> */}
          <Route path="/create-customer" element={<CreateCustomer />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          ></Route>
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <InventoryPage />
              </ProtectedRoute>
            }
          ></Route>
          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute>
                <PaidOnlyRoute>
                  <AdminCustomerPage />
                </PaidOnlyRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <PaidOnlyRoute>
                  <CustomerPage />
                </PaidOnlyRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/sensors" element={<Navigate to="/sensors/list" replace />} />
          <Route
            path="/sensors/list"
            element={
              <ProtectedRoute>
                <SensorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sensors/lookup"
            element={
              <ProtectedRoute>
                <SensorPage />
              </ProtectedRoute>
            }
          />
          {auth?.user?.account_type !== 2 && (
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <PaidOnlyRoute>
                    <UserPage />
                  </PaidOnlyRoute>
                </ProtectedRoute>
              }
            />
          )}
          {/* <Route */}
          {/*   path="/projects_public" */}
          {/*   element={ */}
          {/*       <ProjectMapView /> */}
          {/*   } */}
          {/* /> */}
          {/* <Route
            path="/projects/mapview"
            element={
              <ProtectedRoute>
                <ProjectMapView />
              </ProtectedRoute>
            }
          /> */}
          {/* <Route */}
          {/* 	end */}
          {/* 	path="/dashboard" */}
          {/* 	element={ */}
          {/* 		<ProtectedRoute> */}
          {/* 			<Dashboard /> */}
          {/* 			{/* <Dashboard setCurrentStep={setCurrentStep} /> */}
          {/* 		</ProtectedRoute> */}
          {/* 	} */}
          {/* /> */}
          {/*<Route path="/" redirectTo="/login" />*/}
          {auth?.user?.account_type !== 2 && (
            <Route
              path="/user/:id"
              element={
                <ProtectedRoute>
                  <PaidOnlyRoute>
                    <UserPage />
                  </PaidOnlyRoute>
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="/sensor/:id"
            element={
              <ProtectedRoute>
                <SensorDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/:id"
            element={
              <ProtectedRoute>
                <PaidOnlyRoute>
                  <CustomerPage />
                </PaidOnlyRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/sensors/lookup" replace />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
      {/* </TourProvider> */}
      <ReactQueryDevtools />
      <CookieConsent>
        Diese Website nutzt Cookies, um die User Experience zu verbessern.
      </CookieConsent>
    </QueryClientProvider>
  );
};
export default App;
